// @ts-ignore no types
import { VerificationModal } from '@adc/ui-adc'
import { CombinedListingType } from 'common/context/listing'
import { listingToREOProperty, listingToTPSProperty } from 'server/transformers'
import {
  NOTIFY_CREDIT_BID,
  NOTIFY_RESERVE,
  SIMILAR_PROPERTY_SOURCE,
  TRANSIENT_MESSAGE_NOTIFY_CREDIT_BID_BODY,
  TRANSIENT_MESSAGE_NOTIFY_RESERVE_BODY,
  TRANSIENT_MESSAGE_PROPERTY_SAVED_BODY,
  TRANSIENT_MESSAGE_PROPERTY_SAVED_TITLE,
  TRANSIENT_MESSAGE_PROPERTY_UNSAVED_BODY,
  TRANSIENT_MESSAGE_PROPERTY_UNSAVED_TITLE,
} from 'common/statics'
import * as blueprint from '@adc/blueprint'
import { Listing, Property } from '@adc/sdk/types'
import { getApplicationNotificationsContext } from 'common/utils/applicationNotificationsBridge'
import {
  UPDATE_PROPERTY_SAVE_STATUS,
  UPDATE_SIMILAR_PROPERTIES_SAVE_STATUS,
} from 'common/constants/actionTypes'
import logger from '@adc/logger'
import analytics from 'common/services/analytics'
import { AuctionUser } from '@adc/foundation'

const { actions: verificationModalActions } = VerificationModal

type PropertyFromListing =
  | (ReturnType<typeof listingToREOProperty> & { listing: CombinedListingType })
  | (ReturnType<typeof listingToTPSProperty> & { listing: CombinedListingType })

export const createPropertyFromListing = (listing: CombinedListingType) => {
  const tps = Boolean(listing.event?.trustee_sale)
  const property = tps
    ? // TODO: remove as when transoformers.js file is typed
      (listingToTPSProperty(listing, {}) as PropertyFromListing)
    : (listingToREOProperty(listing, {}) as PropertyFromListing)

  property.listing = listing

  return {
    property,
    tps,
  }
}

export type ToggleSavePropertyArgs = {
  listing: Listing
  property: Property
  source?: string
  justLoggedIn?: boolean
  pushAuthQueueItem: (args: Array<any>) => void
  toggleAuthModal: () => void
  pushOnboardingQueueItem?: (args: Array<any>) => void
  nudgeOptInModal?: (() => void) | null
}

type AnalyticsArgs = {
  listing: Listing
  property: Property
  source: string
  getState: () => any
  save: boolean
}
const handleSavePropertyAnalyticsRepipe = ({
  listing,
  property,
  source,
  getState,
  save = true,
}: AnalyticsArgs) => {
  const {
    propertyAnalytics: { similarProperties },
  } = getState()

  if (source === SIMILAR_PROPERTY_SOURCE) {
    const listing = similarProperties.find(
      (p: { primary_property: { property_id: string } }) =>
        p.primary_property.property_id === property.property_id
    )
    // TODO: MOVE AWAY FROM REDUX
    // Unlike in Non Repipe similarProperty data from analytics in Repipe is missing
    // the some fields previously requested for GA
    // auction_number, auction_id, product_type_original, property_type, property_county, seller_code
    // venue_id, potential_return
    analytics.property.saveSimilarProperty(listing, save)
  } else {
    analytics.property[save ? 'save' : 'removeSaved'](listing)
  }
}

type SaveUnsaveArgs = Omit<
  ToggleSavePropertyArgs,
  'justLoggedIn' | 'pushAuthQueueItem' | 'toggleAuthModal'
> & {
  updateSaveProperty: ReturnType<
    typeof blueprint.user.hooks.useSavedProperties
  >['actions']['updateSaveProperty']
}
const saveProperty = ({
  updateSaveProperty,
  listing,
  property,
  source = '',
  nudgeOptInModal = null,
}: SaveUnsaveArgs) => {
  // @ts-ignore TODO remove redux
  return (dispatch, getState) => {
    handleSavePropertyAnalyticsRepipe({
      listing,
      property,
      source,
      getState,
      save: true,
    })

    const notifications = getApplicationNotificationsContext()

    return (
      updateSaveProperty(property.property_id, true)
        .then(() => {
          let message = TRANSIENT_MESSAGE_PROPERTY_SAVED_BODY

          if (source === NOTIFY_CREDIT_BID) {
            message = TRANSIENT_MESSAGE_NOTIFY_CREDIT_BID_BODY
          } else if (source === NOTIFY_RESERVE) {
            message = TRANSIENT_MESSAGE_NOTIFY_RESERVE_BODY
          }

          const savedAssetsIdentifiers = [
            getState().properties.savedAssetsIdentifiers,
            property.property_id,
          ]

          dispatch({
            type: UPDATE_SIMILAR_PROPERTIES_SAVE_STATUS,
            propertyIds: [...savedAssetsIdentifiers],
          })
          dispatch({
            type: UPDATE_PROPERTY_SAVE_STATUS,
            propertyIds: [...savedAssetsIdentifiers],
          })

          notifications.overlayBanner.open({
            title: TRANSIENT_MESSAGE_PROPERTY_SAVED_TITLE,
            message,
          })

          if (typeof nudgeOptInModal === 'function') {
            nudgeOptInModal()
          }

          // processes the queue upon successful saving of asset
          dispatch(verificationModalActions.processPostVerificationQueue())
        })
        // TODO: implement pushVerificationQueueItem & requireVerification
        // currently, we don't have message for user session expire in graph
        // we will need to work on this when graph starts supporting this
        .catch(err => {
          logger.error(err)
        })
    )
  }
}

const unsaveProperty = ({
  updateSaveProperty,
  listing,
  property,
  source = '',
}: SaveUnsaveArgs) => {
  // @ts-ignore remove redux
  return (dispatch, getState) => {
    handleSavePropertyAnalyticsRepipe({
      listing,
      property,
      source,
      getState,
      save: false,
    })

    const notifications = getApplicationNotificationsContext()

    return updateSaveProperty(property.property_id, false)
      .then(() => {
        const savedAssetsIdentifiers =
          getState().properties.savedAssetsIdentifiers.filter(
            (p: string) => Number(p) !== Number(property.property_id)
          )

        dispatch({
          type: UPDATE_SIMILAR_PROPERTIES_SAVE_STATUS,
          propertyIds: [...savedAssetsIdentifiers],
        })
        dispatch({
          type: UPDATE_PROPERTY_SAVE_STATUS,
          propertyIds: [...savedAssetsIdentifiers],
        })

        notifications.overlayBanner.open({
          title: TRANSIENT_MESSAGE_PROPERTY_UNSAVED_TITLE,
          message: TRANSIENT_MESSAGE_PROPERTY_UNSAVED_BODY,
        })
      })
      .catch(err => {
        logger.error(err)
      })
  }
}

export const toggleSaveProperty =
  (
    updateSaveProperty: ReturnType<
      typeof blueprint.user.hooks.useSavedProperties
    >['actions']['updateSaveProperty'],
    dispatch: any,
    user?: AuctionUser
  ) =>
  (
    {
      listing,
      property,
      source,
      justLoggedIn = false,
      pushAuthQueueItem,
      toggleAuthModal,
      pushOnboardingQueueItem,
      nudgeOptInModal,
    }: ToggleSavePropertyArgs,
    _authType?: any,
    authUser?: any
  ) => {
    // TODO move away from redux
    // @ts-ignore remove redux
    const isLoggedIn = user?.logged_in
    if (justLoggedIn && property.is_currently_saved) {
      return
    }

    if (isLoggedIn || authUser) {
      dispatch(
        (property.is_currently_saved ? unsaveProperty : saveProperty)({
          updateSaveProperty,
          listing,
          property,
          source,
          nudgeOptInModal,
        })
      )
    } else {
      // since user is not authenticated we should not display the opt in modal automatically but rather when the auth flow has completed
      const args = {
        listing,
        property,
        source,
        justLoggedIn: true,
        pushAuthQueueItem,
        toggleAuthModal,
      }
      pushAuthQueueItem &&
        pushAuthQueueItem([
          toggleSaveProperty(updateSaveProperty, dispatch, user),
          [args],
        ])
      if (typeof nudgeOptInModal === 'function' && pushOnboardingQueueItem) {
        pushOnboardingQueueItem([nudgeOptInModal, []])
      }
      toggleAuthModal && toggleAuthModal()
    }
  }
